import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import config from '../config/environment';
import { degreesAPI, groupsAPI } from '../services/api';

const EditGroup = () => {
  const { currentUser } = useAuth();
  
  const [filterData, setFilterData] = useState({
    degreeProgramme: '',
    selectedDegreeId: '',
    academicYear: '',
    year: '',
    semester: '',
    status: 'Active'
  });

  const [degreePrograms, setDegreePrograms] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Load degree programs from the admin's university
  useEffect(() => {
    const loadDegreePrograms = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        console.log('🎓 Loading degree programs using environment-based API...');

        const response = await degreesAPI.getAllDegrees();
        const degreeData = response.data;

        // Format degree programs for display: "degree_programme (local_international)"
        const formattedDegreePrograms = degreeData?.map(degree => ({
          id: degree.id,
          displayName: `${degree.degree_programme} (${degree.local_international})`,
          originalData: degree
        })) || [];

        console.log(`✅ Loaded ${formattedDegreePrograms.length} degree programs from ${config.getApiUrl('/degrees')}`);
        setDegreePrograms(formattedDegreePrograms);
        
      } catch (error) {
        console.error('❌ Error loading degree programs from API:', error);
        alert('Error loading degree programs: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadDegreePrograms();
    }
  }, [currentUser]);

  // Cascading filter effect - when filter criteria change, fetch matching groups
  useEffect(() => {
    const fetchFilteredGroups = async () => {
      if (!filterData.selectedDegreeId || !filterData.academicYear || !filterData.year || !filterData.semester) {
        setFilteredGroups([]);
        setSelectedGroups([]);
        return;
      }

      try {
        console.log('🔍 Fetching filtered groups with criteria:', {
          degree_details_id: filterData.selectedDegreeId,
          academic_year: filterData.academicYear,
          year: filterData.year,
          semester: filterData.semester
        });

        // Use environment-based API to get filtered groups
        const response = await groupsAPI.getAllGroups();
        const allGroups = response.data;

        // Filter groups based on the selected criteria
        const matchingGroups = allGroups.filter(group => {
          return group.degree_details_id === filterData.selectedDegreeId &&
                 group.academic_year === filterData.academicYear &&
                 group.year === parseInt(filterData.year) &&
                 group.semester === parseInt(filterData.semester);
        });

        console.log(`✅ Found ${matchingGroups.length} matching groups for the criteria`);
        setFilteredGroups(matchingGroups);
        setSelectedGroups([]); // Reset selected groups when filter changes
        
      } catch (error) {
        console.error('❌ Error fetching filtered groups:', error);
        alert('Error loading groups: ' + error.message);
      }
    };

    fetchFilteredGroups();
  }, [filterData.selectedDegreeId, filterData.academicYear, filterData.year, filterData.semester]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));

    // Handle degree program selection - store both display name and ID
    if (name === 'degreeProgramme') {
      const selectedDegree = degreePrograms.find(degree => degree.displayName === value);
      setFilterData(prev => ({
        ...prev,
        selectedDegreeId: selectedDegree ? selectedDegree.id : '',
        degreeProgramme: value
      }));
    }
  };

  const handleGroupSelection = (e) => {
    const { value, checked } = e.target;
    
    if (checked) {
      setSelectedGroups(prev => [...prev, value]);
    } else {
      setSelectedGroups(prev => prev.filter(groupId => groupId !== value));
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    
    if (selectedGroups.length === 0) {
      alert('Please select at least one group to update');
      return;
    }

    if (!filterData.status) {
      alert('Please select a status');
      return;
    }

    try {
      setUpdating(true);
      console.log('🔄 Updating groups status using environment-based API...');
      console.log(`Selected groups: ${selectedGroups.length}`);
      console.log(`New status: ${filterData.status}`);

      const statusValue = filterData.status === 'Active';
      
      // Update each selected group
      const updatePromises = selectedGroups.map(async (groupId) => {
        try {
          const response = await groupsAPI.updateGroup(groupId, { 
            active: statusValue 
          });
          console.log(`✅ Updated group ${groupId} to ${filterData.status}`);
          return response.data;
        } catch (error) {
          console.error(`❌ Failed to update group ${groupId}:`, error);
          throw error;
        }
      });

      await Promise.all(updatePromises);
      
      alert(`Successfully updated ${selectedGroups.length} group(s) to ${filterData.status} status!`);
      
      // Refresh the filtered groups to show updated data
      const response = await groupsAPI.getAllGroups();
      const allGroups = response.data;
      const matchingGroups = allGroups.filter(group => {
        return group.degree_details_id === filterData.selectedDegreeId &&
               group.academic_year === filterData.academicYear &&
               group.year === parseInt(filterData.year) &&
               group.semester === parseInt(filterData.semester);
      });
      
      setFilteredGroups(matchingGroups);
      setSelectedGroups([]);
      
    } catch (error) {
      console.error('❌ Error updating group status:', error);
      alert('Error updating groups: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        {!currentUser ? (
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Please Log In
            </h2>
            <p style={{fontSize: 16, fontFamily: 'Outfit', color: '#77838F'}}>
              You need to be logged in as an admin to edit groups.
            </p>
          </div>
        ) : (
        <>
        {/* Page Title */}
        <h1 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', marginTop: '20px', paddingLeft: '40px'}}>
          Edit Group
        </h1>

        {/* Loading Overlay */}
        {(loading || updating) && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              background: 'white',
              padding: '20px 40px',
              borderRadius: 8,
              textAlign: 'center',
              fontSize: 16,
              fontFamily: 'Outfit',
              color: '#F04915'
            }}>
              {loading ? 'Loading degree programs...' : 'Updating groups...'}
            </div>
          </div>
        )}

        {/* Filter Form */}
        <form onSubmit={handleUpdateStatus}>
          <div style={{marginTop: '20px', margin: '20px 40px'}}>
            <div style={{background: '#F04915', padding: '20px', borderTopLeftRadius: 15, borderTopRightRadius: 15}}>
              <h2 style={{color: 'white', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', margin: 0}}>
                Edit Student Groups
              </h2>
              <p style={{color: 'white', fontSize: 14, fontFamily: 'Outfit', fontWeight: '400', margin: '10px 0 0 0'}}>
                Filter groups by criteria, select multiple groups, and update their status
              </p>
            </div>

            <div style={{
              background: '#FFFBF5', 
              padding: '40px',
              borderBottomLeftRadius: 15,
              borderBottomRightRadius: 15
            }}>
              {/* Filter Row 1: Degree Programme, Academic Year */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Degree Programme <span style={{color: '#F04915'}}>*</span>
                  </label>
                  <select
                    name="degreeProgramme"
                    value={filterData.degreeProgramme}
                    onChange={handleFilterChange}
                    required
                    style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                  >
                    <option value="">Select Degree Programme</option>
                    {degreePrograms.map(program => (
                      <option key={program.id} value={program.displayName}>
                        {program.displayName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Academic Year <span style={{color: '#F04915'}}>*</span>
                  </label>
                  <select
                    name="academicYear"
                    value={filterData.academicYear}
                    onChange={handleFilterChange}
                    required
                    style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                  >
                    <option value="">Select Academic Year</option>
                    <option value="2023-2024">2023-2024</option>
                    <option value="2024-2025">2024-2025</option>
                    <option value="2025-2026">2025-2026</option>
                    <option value="2026-2027">2026-2027</option>
                    <option value="2027-2028">2027-2028</option>
                  </select>
                </div>
              </div>

              {/* Filter Row 2: Year, Semester */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Year <span style={{color: '#F04915'}}>*</span>
                  </label>
                  <select
                    name="year"
                    value={filterData.year}
                    onChange={handleFilterChange}
                    required
                    style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                  >
                    <option value="">Select Year</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                  </select>
                </div>

                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Semester <span style={{color: '#F04915'}}>*</span>
                  </label>
                  <select
                    name="semester"
                    value={filterData.semester}
                    onChange={handleFilterChange}
                    required
                    style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                  >
                    <option value="">Select Semester</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                  </select>
                </div>
              </div>

              {/* Filter Row 3: Status */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px'}}>
                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Status <span style={{color: '#F04915'}}>*</span>
                  </label>
                  <select
                    name="status"
                    value={filterData.status}
                    onChange={handleFilterChange}
                    required
                    style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div></div> {/* Empty div for grid alignment */}
              </div>

              {/* Groups Selection Area */}
              {filteredGroups.length > 0 && (
                <div style={{marginTop: '30px'}}>
                  <h3 style={{color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '600', marginBottom: '15px'}}>
                    Select Groups to Update ({filteredGroups.length} found)
                  </h3>
                  
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    padding: '15px',
                    background: 'white'
                  }}>
                    {filteredGroups.map(group => (
                      <div key={group.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px',
                        borderBottom: '1px solid #F0F0F0',
                        marginBottom: '5px'
                      }}>
                        <input
                          type="checkbox"
                          id={group.id}
                          value={group.id}
                          checked={selectedGroups.includes(group.id)}
                          onChange={handleGroupSelection}
                          style={{marginRight: '15px', transform: 'scale(1.2)'}}
                        />
                        <label htmlFor={group.id} style={{
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          color: 'black',
                          cursor: 'pointer',
                          flex: 1
                        }}>
                          <strong>{group.group_name}</strong>
                          <span style={{color: '#77838F', marginLeft: '10px'}}>
                            (Year {group.year}, Semester {group.semester}, {group.academic_year})
                            - Status: {group.active ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>

                  {selectedGroups.length > 0 && (
                    <div style={{marginTop: '15px', padding: '10px', background: '#E8F5E8', borderRadius: 8}}>
                      <p style={{margin: 0, fontSize: 14, fontFamily: 'Outfit', color: '#2D5016'}}>
                        <strong>{selectedGroups.length}</strong> group(s) selected for status update to <strong>{filterData.status}</strong>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Message when no groups found */}
              {filteredGroups.length === 0 && filterData.selectedDegreeId && filterData.academicYear && filterData.year && filterData.semester && (
                <div style={{
                  textAlign: 'center',
                  padding: '30px',
                  background: '#FFF3CD',
                  borderRadius: 8,
                  marginTop: '20px'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: 16,
                    fontFamily: 'Outfit',
                    color: '#856404'
                  }}>
                    No groups found matching the selected criteria.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{display: 'flex', justifyContent: 'space-between', margin: '40px'}}>
            <Link to="/academic-management">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back to Academic Management
              </button>
            </Link>
            
            <button 
              type="submit" 
              disabled={selectedGroups.length === 0 || updating}
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: selectedGroups.length === 0 ? '#CCC' : '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: selectedGroups.length === 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                if (selectedGroups.length > 0) {
                  e.target.style.background = '#D63A0F';
                }
              }}
              onMouseOut={(e) => {
                if (selectedGroups.length > 0) {
                  e.target.style.background = '#F04915';
                }
              }}
            >
              {updating ? 'Updating...' : 'Update Selected Groups'}
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

export default EditGroup;
